import { useEffect, useState } from 'react';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { refreshDatabaseSchema } from '../utils/schemaRefresh';

export const useInitialData = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading: authLoading } = useAuth();
  
  const { 
    fetchClients, 
    fetchSuppliers, 
    fetchInvoices, 
    fetchPayments,
    fetchProductions,
    fetchProductionTasks,
    fetchSampleTasks,
    fetchSamples,
    fetchExpress,
    fetchExpressItems,
    fetchExpressStock,
    fetchExpressStockMovements,
    fetchExpressSamples,
    fetchExpressSampleItems,
    fetchTodoItems,
    fetchChatMessages,
    fetchExpenseCategories,
    fetchExpenses,
    setError: setAppError
  } = useAppStore();

  // Helper function to safely execute fetch operations
  const safeFetch = async (fetchFn: () => Promise<void>, name: string) => {
    try {
      await fetchFn();
    } catch (error) {
      console.error(`Error fetching ${name}:`, error);
      // Don't throw the error so other fetches can continue
    }
  };
  useEffect(() => {
    // Wait for auth to finish loading before attempting to load data
    if (authLoading) {
      return;
    }
    
    const loadData = async () => {
      // If no user is logged in, don't try to fetch data
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Set a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          if (isLoading) {
            setError('Tiempo de carga excedido. Verifique su conexión a internet e intente nuevamente.');
            setIsLoading(false);
          }
        }, 20000); // 20 seconds timeout
        
        // First refresh the database schema (with error handling)
        try {
          await refreshDatabaseSchema();
        } catch (error) {
          console.error('Error refreshing database schema:', error);
          // Continue with data fetching even if schema refresh fails
        }
        
        // Then fetch clients as they are required by other components
        try {
          await fetchClients();
        } catch (error) {
          console.error('Error fetching clients:', error);
          setError('Error al conectar con la base de datos. Verifique su conexión a internet.');
          clearTimeout(timeoutId);
          setIsLoading(false);
          return;
        }
        
        // Then fetch the rest of the data in parallel
        await Promise.all([
          safeFetch(fetchSuppliers, 'suppliers'),
          safeFetch(fetchInvoices, 'invoices'),
          safeFetch(fetchPayments, 'payments'),
          safeFetch(fetchProductions, 'productions'),
          safeFetch(fetchProductionTasks, 'production tasks'),
          safeFetch(fetchSampleTasks, 'sample tasks'),
          safeFetch(fetchSamples, 'samples'),
          safeFetch(fetchExpress, 'express orders'),
          safeFetch(fetchExpressItems, 'express items'),
          safeFetch(fetchExpressStock, 'express stock'),
          safeFetch(fetchExpressStockMovements, 'express stock movements'),
          safeFetch(fetchExpressSamples, 'express samples'),
          safeFetch(fetchExpressSampleItems, 'express sample items'),
          safeFetch(fetchExpenseCategories, 'expense categories'),
          safeFetch(fetchExpenses, 'expenses'),
          safeFetch(fetchTodoItems, 'todo items'),
          safeFetch(() => fetchChatMessages('general'), 'chat messages')
        ]);
        
        // Clear the timeout since data loaded successfully
        clearTimeout(timeoutId);
      } catch (error: any) {
        const errorMessage = error.message?.includes('fetch') 
          ? 'Error de conexión. Verifique su conexión a internet e intente nuevamente.'
          : error.message || 'Error al cargar los datos iniciales';
        setError(errorMessage);
        setAppError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [
    user,
    authLoading,
    fetchClients, 
    fetchSuppliers, 
    fetchInvoices, 
    fetchPayments, 
    fetchProductions,
    fetchProductionTasks,
    fetchSampleTasks,
    fetchSamples,
    fetchExpress,
    fetchExpressItems,
    fetchExpressStock,
    fetchExpressStockMovements,
    fetchExpressSamples,
    fetchExpressSampleItems,
    fetchTodoItems,
    fetchChatMessages,
    fetchExpenseCategories,
    fetchExpenses,
    setAppError
  ]);

  return { isLoading, error };
};